import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Box,
  Chip,
  Collapse,
  Button,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Event as EventIcon,
  Payment as PaymentIcon,
  Notes as NotesIcon,
  Business as BusinessIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import { THEME_COLORS } from '../../constants';

/**
 * Componente de tabla de suscripciones para vista de escritorio
 */
const SubscriptionsTable = ({
  subscriptions,
  expandedSubscriptionId,
  onExpand,
  onEdit,
  onDelete,
  showActions = true,
  // Props de paginación
  pagination = null,
  onPageChange = null,
  onPageSizeChange = null,
}) => {
  const handleExpandClick = (subscriptionId) => {
    onExpand(subscriptionId);
  };

  const handleEditClick = (e, subscription) => {
    e.stopPropagation();
    onEdit(subscription);
  };

  const handleDeleteClick = (e, subscription) => {
    e.stopPropagation();
    onDelete(subscription);
  };

  // Determinar el color del estado - Solo tema minimalista blanco/negro/gris
  const getStatusColor = (status, isExpired, isExpiringSoon) => {
    if (isExpired || status === 'cancelada') {
      return THEME_COLORS.TEXT_SECONDARY; // Gris oscuro para vencidas/canceladas
    }
    if (isExpiringSoon || status === 'suspendida') {
      return THEME_COLORS.TEXT_MUTED; // Gris medio para suspendidas/venciendo
    }
    if (status === 'activa') {
      return THEME_COLORS.TEXT_PRIMARY; // Negro para activas
    }
    return THEME_COLORS.TEXT_SECONDARY; // Gris por defecto
  };

  // Obtener texto del estado con indicadores adicionales
  const getStatusText = (subscription) => {
    if (subscription.isExpired) return 'Vencida';
    if (subscription.isExpiringSoon) return `Vence en ${subscription.daysRemaining} días`;
    return subscription.statusLabel;
  };

  // Componente de controles de paginación
  const PaginationControls = () => {
    if (!pagination) return null;

    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: 2,
          borderTop: `1px solid ${THEME_COLORS.BORDER_LIGHT}`,
          backgroundColor: THEME_COLORS.BACKGROUND_DEFAULT,
        }}
      >
        {/* Información de registros */}
        <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_SECONDARY }}>
          Mostrando {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total} suscripciones
        </Typography>

        {/* Controles de navegación */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Selector de elementos por página */}
          <FormControl size="small" sx={{ minWidth: 80 }}>
            <Select
              value={pagination.limit}
              onChange={(e) => onPageSizeChange?.(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: THEME_COLORS.BACKGROUND_PAPER,
                },
                '& .MuiSelect-select': {
                  color: THEME_COLORS.TEXT_PRIMARY,
                  fontSize: '0.875rem',
                },
              }}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
            </Select>
          </FormControl>

          {/* Botones de navegación */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              disabled={!pagination.hasPrev}
              onClick={() => onPageChange?.(pagination.page - 1)}
              startIcon={<NavigateBeforeIcon />}
              sx={{
                color: THEME_COLORS.TEXT_SECONDARY,
                borderColor: THEME_COLORS.BORDER_LIGHT,
                borderRadius: '8px',
                textTransform: 'none',
                '&:hover': {
                  borderColor: THEME_COLORS.TEXT_PRIMARY,
                  color: THEME_COLORS.TEXT_PRIMARY,
                },
                '&:disabled': {
                  color: THEME_COLORS.TEXT_MUTED,
                  borderColor: THEME_COLORS.BORDER_LIGHT,
                },
              }}
            >
              Anterior
            </Button>

            <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_PRIMARY, px: 2 }}>
              Página {pagination.page} de {pagination.pages || 1}
            </Typography>

            <Button
              variant="outlined"
              size="small"
              disabled={!pagination.hasNext}
              onClick={() => onPageChange?.(pagination.page + 1)}
              endIcon={<NavigateNextIcon />}
              sx={{
                color: THEME_COLORS.TEXT_SECONDARY,
                borderColor: THEME_COLORS.BORDER_LIGHT,
                borderRadius: '8px',
                textTransform: 'none',
                '&:hover': {
                  borderColor: THEME_COLORS.TEXT_PRIMARY,
                  color: THEME_COLORS.TEXT_PRIMARY,
                },
                '&:disabled': {
                  color: THEME_COLORS.TEXT_MUTED,
                  borderColor: THEME_COLORS.BORDER_LIGHT,
                },
              }}
            >
              Siguiente
            </Button>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      <TableContainer
        component={Paper}
        sx={{
          backgroundColor: THEME_COLORS.BACKGROUND_PAPER,
          border: `1px solid ${THEME_COLORS.BORDER_LIGHT}`,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: THEME_COLORS.BACKGROUND_DEFAULT }}>
              <TableCell sx={{ width: 40 }}></TableCell>
              <TableCell sx={{ fontWeight: 600, color: THEME_COLORS.TEXT_PRIMARY }}>
                Contacto
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: THEME_COLORS.TEXT_PRIMARY }}>
                Distribuidor
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: THEME_COLORS.TEXT_PRIMARY }}>
                Estado
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: THEME_COLORS.TEXT_PRIMARY }}>
                Plan
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: THEME_COLORS.TEXT_PRIMARY }}>
                Fecha Fin
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: THEME_COLORS.TEXT_PRIMARY }}>
                Monto
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: THEME_COLORS.TEXT_PRIMARY }}>
                Pagador
              </TableCell>
              {showActions && (
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: THEME_COLORS.TEXT_PRIMARY,
                    textAlign: 'center',
                    width: 120,
                  }}
                >
                  Acciones
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {subscriptions.map((subscription) => {
              const isExpanded = expandedSubscriptionId === subscription.subscription_id;
              
              return (
                <React.Fragment key={subscription.subscription_id}>
                  {/* Fila principal */}
                  <TableRow
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: THEME_COLORS.BACKGROUND_PAPER,
                      '&:hover': {
                        backgroundColor: THEME_COLORS.BACKGROUND_DEFAULT,
                      },
                      borderBottom: isExpanded 
                        ? 'none' 
                        : `1px solid ${THEME_COLORS.BORDER_LIGHT}`,
                    }}
                    onClick={() => handleExpandClick(subscription.subscription_id)}
                  >
                    {/* Botón expandir */}
                    <TableCell sx={{ p: 1 }}>
                      <IconButton
                        size="small"
                        sx={{
                          color: THEME_COLORS.TEXT_SECONDARY,
                          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s ease-in-out',
                        }}
                      >
                        <ExpandMoreIcon fontSize="small" />
                      </IconButton>
                    </TableCell>

                    {/* Contacto */}
                    <TableCell>
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: THEME_COLORS.TEXT_PRIMARY,
                            lineHeight: 1.3,
                          }}
                        >
                          {subscription.contact_name}
                        </Typography>
                        {subscription.contact_phone && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: THEME_COLORS.TEXT_SECONDARY,
                              display: 'block',
                            }}
                          >
                            {subscription.contact_phone}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>

                    {/* Distribuidor */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BusinessIcon sx={{ fontSize: 16, color: THEME_COLORS.TEXT_SECONDARY }} />
                        <Typography
                          variant="body2"
                          sx={{
                            color: THEME_COLORS.TEXT_PRIMARY,
                            fontSize: '0.875rem',
                          }}
                        >
                          {subscription.distributor_empresa || 'Sin asignar'}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* Estado */}
                    <TableCell>
                      <Chip
                        label={getStatusText(subscription)}
                        size="small"
                        sx={{
                          backgroundColor: getStatusColor(
                            subscription.estado,
                            subscription.isExpired,
                            subscription.isExpiringSoon
                          ),
                          color: 'white',
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          minWidth: 80,
                        }}
                      />
                    </TableCell>

                    {/* Plan */}
                    <TableCell>
                      <Chip
                        label={subscription.planLabel}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderColor: THEME_COLORS.BORDER_LIGHT,
                          color: THEME_COLORS.TEXT_SECONDARY,
                          fontSize: '0.75rem',
                        }}
                      />
                    </TableCell>

                    {/* Fecha Fin */}
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          color: subscription.isExpired || subscription.isExpiringSoon
                            ? getStatusColor(subscription.estado, subscription.isExpired, subscription.isExpiringSoon)
                            : THEME_COLORS.TEXT_PRIMARY,
                          fontWeight: subscription.isExpired || subscription.isExpiringSoon ? 600 : 400,
                        }}
                      >
                        {subscription.formattedEndDate}
                      </Typography>
                    </TableCell>

                    {/* Monto */}
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          color: subscription.formattedAmount !== 'Sin monto' 
                            ? THEME_COLORS.TEXT_PRIMARY 
                            : THEME_COLORS.TEXT_SECONDARY,
                          fontWeight: subscription.formattedAmount !== 'Sin monto' ? 600 : 400,
                        }}
                      >
                        {subscription.formattedAmount}
                      </Typography>
                    </TableCell>

                    {/* Pagador */}
                    <TableCell>
                      <Box>
                        {subscription.is_payer ? (
                          <Chip
                            label="Él mismo"
                            size="small"
                            sx={{
                              backgroundColor: THEME_COLORS.TEXT_PRIMARY,
                              color: 'white',
                              fontSize: '0.75rem',
                              fontWeight: 500,
                            }}
                          />
                        ) : (
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{
                                color: THEME_COLORS.TEXT_PRIMARY,
                                fontWeight: 600,
                                lineHeight: 1.3,
                              }}
                            >
                              {subscription.payer_name || 'No especificado'}
                            </Typography>
                            {subscription.payer_phone && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: THEME_COLORS.TEXT_SECONDARY,
                                  display: 'block',
                                }}
                              >
                                {subscription.payer_phone}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                    </TableCell>

                    {/* Acciones */}
                    {showActions && (
                      <TableCell>
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                          <IconButton
                            size="small"
                            onClick={(e) => handleEditClick(e, subscription)}
                            sx={{
                              color: THEME_COLORS.TEXT_SECONDARY,
                              '&:hover': { color: THEME_COLORS.PRIMARY },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => handleDeleteClick(e, subscription)}
                            sx={{
                              color: THEME_COLORS.TEXT_SECONDARY,
                              '&:hover': { color: THEME_COLORS.ERROR },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>

                  {/* Fila expandible con detalles */}
                  <TableRow>
                    <TableCell colSpan={showActions ? 8 : 7} sx={{ padding: 0, border: 'none' }}>
                      <Collapse in={isExpanded}>
                        <Box sx={{ 
                          padding: 3, 
                          backgroundColor: THEME_COLORS.BACKGROUND_DEFAULT, 
                          borderTop: `1px solid ${THEME_COLORS.BORDER_LIGHT}`,
                        }}>
                          <Typography variant="subtitle2" sx={{ marginBottom: 2, fontWeight: 600, color: THEME_COLORS.TEXT_PRIMARY }}>
                            Detalles de la Suscripción
                          </Typography>
                          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
                            <Box>
                              <Typography variant="caption" sx={{ color: THEME_COLORS.TEXT_SECONDARY }}>
                                ID de Suscripción
                              </Typography>
                              <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_PRIMARY, fontWeight: 500 }}>
                                {subscription.subscription_id}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" sx={{ color: THEME_COLORS.TEXT_SECONDARY }}>
                                Plan
                              </Typography>
                              <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_PRIMARY, fontWeight: 500 }}>
                                {subscription.planLabel}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" sx={{ color: THEME_COLORS.TEXT_SECONDARY }}>
                                Estado
                              </Typography>
                              <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_PRIMARY, fontWeight: 500 }}>
                                {subscription.statusLabel}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" sx={{ color: THEME_COLORS.TEXT_SECONDARY }}>
                                Fecha de Inicio
                              </Typography>
                              <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_PRIMARY, fontWeight: 500 }}>
                                {subscription.formattedStartDate}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" sx={{ color: THEME_COLORS.TEXT_SECONDARY }}>
                                Fecha de Fin
                              </Typography>
                              <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_PRIMARY, fontWeight: 500 }}>
                                {subscription.formattedEndDate}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" sx={{ color: THEME_COLORS.TEXT_SECONDARY }}>
                                Monto
                              </Typography>
                              <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_PRIMARY, fontWeight: 500 }}>
                                {subscription.formattedAmount}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" sx={{ color: THEME_COLORS.TEXT_SECONDARY }}>
                                Método de Pago
                              </Typography>
                              <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_PRIMARY, fontWeight: 500 }}>
                                {subscription.metodo_pago || 'No especificado'}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" sx={{ color: THEME_COLORS.TEXT_SECONDARY }}>
                                Pagador
                              </Typography>
                              <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_PRIMARY, fontWeight: 500 }}>
                                {subscription.is_payer 
                                  ? 'Él mismo' 
                                  : subscription.payer_name || 'No especificado'
                                }
                              </Typography>
                            </Box>
                            {subscription.notas && (
                              <Box>
                                <Typography variant="caption" sx={{ color: THEME_COLORS.TEXT_SECONDARY }}>
                                  Notas
                                </Typography>
                                <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_PRIMARY, fontWeight: 500 }}>
                                  {subscription.notas}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <PaginationControls />
    </Box>
  );
};

export default SubscriptionsTable; 